import React, { useState } from 'react';
import { usePageMeta } from '../lib/usePageMeta';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import PrincipleBlock from '../components/PrincipleBlock';
import CTABar from '../components/CTABar';
import InterestFormModal from '../components/InterestFormModal';

const PRINCIPLES = [
  {
    number: '01',
    title: 'Deterministic, Not Probabilistic',
    body: 'Every calculation traces to source. Versioned rules, auditable logic, reproducible outputs. The system you ship can survive a regulator\'s inspection on day one and on day three hundred.',
  },
  {
    number: '02',
    title: 'Precision Over Speed',
    body: 'Built for defensibility. If the output cannot survive a CFO\'s scrutiny, it is not ready. We will say so on the first call rather than ship something that frays under pressure.',
  },
  {
    number: '03',
    title: 'Designed to Hand Off',
    body: 'Built so your team can own and run the system after we leave. No vendor lock-in. No "we\'ll come back next quarter." Every engagement ends with an internal owner who can extend the system the morning after our exit.',
    example: 'Hand-off deliverables include runbooks, systems diagrams, the model-swap policy, and rollback paths, each tested with the named internal owner before close.',
  },
  {
    number: '04',
    title: 'Compliance by Architecture',
    body: 'SOC 2, HIPAA, and GDPR considerations are baked into the foundation, not bolted on. Regulated data stays inside your existing controls. The audit trail is a deliverable, not an afterthought.',
    example: 'The audit trail is part of the build, not a retrofit. The same patterns ship into FDA, HIPAA, GDPR, and SOC 2 environments without rework.',
  },
];

const Why: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  usePageMeta(
    'Why Parsant | Revenue Systems and AI Automation',
    'Revenue moves three ways and most companies only measure one. Parsant maps all three, then automates the step that actually limits throughput.'
  );

  return (
    <main className="bg-void-black min-h-screen">
      <Nav onContactClick={() => setIsFormOpen(true)} />

      <section className="pt-32 pb-16">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow eyebrow-accent mb-4">Why Parsant</div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-signal-white mb-6 tracking-tight leading-[1.08] max-w-4xl">
            Why revenue systems, and why automation.
          </h1>
          <p className="text-xl text-steel measure">
            This work pays for itself only when you know where revenue actually moves and which
            step is holding it up.
          </p>
        </div>
      </section>

      <section className="section-tight border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6 space-y-8">
          <div className="eyebrow">01 · Why revenue systems</div>
          <h2 className="text-3xl md:text-4xl font-bold text-signal-white max-w-4xl">Revenue moves three ways. Most companies measure one.</h2>
          <div className="text-steel text-lg leading-relaxed space-y-4 measure">
            <p>Companies count the deals they win and estimate the rest. Revenue actually moves through acquisition, retention, and expansion, and the last two compound while the first resets every quarter. A pipeline that only tracks new logos hides where the recurring revenue actually sits.</p>
            <p>We map how revenue moves through your company end to end, settle what each stage means so three teams stop counting the same deal three ways, and instrument the handoffs where deals stall. The forecast steadies first. Then the renewals and expansions nobody was watching start showing up as a number somebody owns.</p>
          </div>
        </div>
      </section>

      <section className="section-tight border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6 space-y-8">
          <div className="eyebrow">02 · Why AI automation</div>
          <h2 className="text-3xl md:text-4xl font-bold text-signal-white max-w-4xl">Automate the constraint that actually limits throughput.</h2>
          <div className="text-steel text-lg leading-relaxed space-y-4 measure">
            <p>Automating a step that was never the bottleneck makes a team feel faster and changes nothing downstream. The constraint sits somewhere specific, and it is rarely where the loudest complaint is coming from.</p>
            <p>We find the step that actually limits throughput, then build the automation around it. Usually that is work nobody wants to own, reconciling records, chasing a definition, rebuilding the same report every Monday. Because healthcare and financial services are where we work by default, those run unattended inside your existing stack with the audit trail intact. At Hand-off a named internal owner runs the automation.</p>
          </div>
        </div>
      </section>

      <section className="section-loose border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">03 · How we build</div>
          <h2 className="text-3xl md:text-4xl font-bold text-signal-white mb-4">Four principles we build by.</h2>
          <p className="text-lg text-steel mb-12 max-w-2xl">
            How Parsant chooses what to ship, when to refuse, and how the system holds up after we hand it off.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PRINCIPLES.map(p => (
              <PrincipleBlock key={p.number} variant="full" {...p} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-cta border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <CTABar onContactClick={() => setIsFormOpen(true)} />
        </div>
      </section>

      <Footer />
      <InterestFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </main>
  );
};

export default Why;
